import type { LobbySeat, RoomOptions } from '../net/protocol';
import { MODES, type ModeId } from '../sim/modes/modes';

/**
 * Online room lobby (Phase 10 / 12): room code + invite link, players with
 * ready state and team, host picks mode / teams and starts. Plain DOM,
 * touch-first (44 px targets), works in portrait and landscape.
 */
export class Lobby {
  private readonly root: HTMLElement;
  private seats: readonly LobbySeat[] = [];
  private options: RoomOptions = { mode: 'classic', teams: 'ffa' };
  private me = '';
  private ready = false;

  constructor(
    parent: HTMLElement,
    private readonly room: string,
    private readonly handlers: {
      ready(on: boolean): void;
      options(o: RoomOptions): void;
      start(): void;
      leave(): void;
    },
  ) {
    this.root = document.createElement('section');
    this.root.className = 'lobby';
    this.root.dataset.testid = 'lobby';
    parent.appendChild(this.root);
    this.root.addEventListener('click', (e) => this.onClick(e));
    this.root.addEventListener('change', (e) => this.onChange(e));
    this.render();
  }

  setMe(playerId: string): void {
    this.me = playerId;
    this.render();
  }

  setStatus(text: string): void {
    const el = this.root.querySelector<HTMLElement>('[data-testid="lobby-status"]');
    if (el) el.textContent = text;
  }

  update(seats: readonly LobbySeat[], options: RoomOptions): void {
    this.seats = seats;
    this.options = options;
    const mine = seats.find((s) => s.playerId === this.me);
    if (mine) this.ready = mine.ready;
    this.render();
  }

  destroy(): void {
    this.root.remove();
  }

  private get isHost(): boolean {
    return this.seats.find((s) => s.playerId === this.me)?.host ?? false;
  }

  private render(): void {
    const mode = MODES[this.options.mode];
    const link = `${location.origin}${location.pathname}?room=${this.room}${new URLSearchParams(location.search).get('server') ? `&server=${encodeURIComponent(new URLSearchParams(location.search).get('server')!)}` : ''}`;
    const host = this.isHost;
    const enough = this.seats.length >= Math.max(2, mode.players.min);
    const allReady = this.seats.every((s) => s.ready);
    this.root.innerHTML = `
      <div class="lobby-card">
        <h1 class="lobby-title">After the Fall</h1>
        <p class="muted">Online room</p>
        <div class="lobby-code" data-testid="room-code">${this.room}</div>
        <button class="btn" data-action="copy-link" data-testid="copy-link" data-link="${escapeAttr(link)}">Copy invite link</button>
        <div class="lobby-row">
          <label>Mode
            <select data-testid="lobby-mode" data-field="mode" ${host ? '' : 'disabled'}>
              ${(['classic', 'last_colony', 'sandbox'] as ModeId[]).map((m) => `<option value="${m}" ${m === this.options.mode ? 'selected' : ''}>${MODES[m].name}</option>`).join('')}
            </select>
          </label>
          <label>Teams
            <select data-testid="lobby-teams" data-field="teams" ${host ? '' : 'disabled'}>
              <option value="ffa" ${this.options.teams === 'ffa' ? 'selected' : ''}>Free for all</option>
              <option value="2v2" ${this.options.teams === '2v2' ? 'selected' : ''}>2 v 2</option>
            </select>
          </label>
        </div>
        <p class="muted small" data-testid="lobby-tagline">${mode.tagline} · ${mode.players.min === mode.players.max ? mode.players.max : `${Math.max(2, mode.players.min)}–${mode.players.max}`} players</p>
        <ul class="lobby-seats" data-testid="lobby-seats">
          ${this.seats
            .map(
              (s) => `<li data-testid="seat-${s.seat}" class="${s.playerId === this.me ? 'is-me' : ''}">
                <span class="seat-name">${escapeHtml(s.name)}${s.host ? ' <span class="muted">(host)</span>' : ''}${s.playerId === this.me ? ' <span class="muted">(you)</span>' : ''}</span>
                <span class="seat-team">${this.options.teams === '2v2' ? `Team ${s.team + 1}` : ''}</span>
                <span class="seat-state ${s.connected ? (s.ready ? 'ok' : '') : 'bad'}">${s.connected ? (s.ready ? 'Ready' : 'Not ready') : 'Disconnected'}</span>
              </li>`,
            )
            .join('')}
        </ul>
        <p class="lobby-status muted" data-testid="lobby-status" aria-live="polite">${host ? (enough ? (allReady ? 'Everyone is ready.' : 'Waiting for players to be ready…') : 'Share the code — waiting for players…') : 'Waiting for the host to start…'}</p>
        <div class="lobby-actions">
          ${
            host
              ? `<button class="btn confirm" data-action="start" data-testid="lobby-start" ${enough && allReady ? '' : 'disabled'}>Start match</button>`
              : `<button class="btn ${this.ready ? 'is-on' : ''}" data-action="ready" data-testid="lobby-ready" aria-pressed="${this.ready}">${this.ready ? 'Ready ✓' : "I'm ready"}</button>`
          }
          <button class="btn" data-action="leave" data-testid="lobby-leave">Leave</button>
        </div>
      </div>`;
  }

  private onClick(e: MouseEvent): void {
    const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-action]');
    if (!btn) return;
    switch (btn.dataset.action) {
      case 'ready':
        this.handlers.ready(!this.ready);
        break;
      case 'start':
        this.handlers.start();
        break;
      case 'leave':
        this.handlers.leave();
        break;
      case 'copy-link':
        void navigator.clipboard?.writeText(btn.dataset.link ?? '').then(
          () => this.setStatus('Invite link copied.'),
          () => this.setStatus(btn.dataset.link ?? ''),
        );
        break;
    }
  }

  private onChange(e: Event): void {
    const el = e.target as HTMLSelectElement;
    if (el.dataset.field === 'mode') this.handlers.options({ ...this.options, mode: el.value as ModeId });
    if (el.dataset.field === 'teams') this.handlers.options({ ...this.options, teams: el.value as 'ffa' | '2v2' });
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

function escapeAttr(s: string): string {
  return escapeHtml(s);
}
